// License admin API wrappers.

import { getData, patchData, postData } from '../../lib/api-client';

export type LicenseStatus = 'pending' | 'active' | 'expired' | 'revoked';
export type LicensePlan = 'basic' | 'pro' | 'enterprise';
export type PaymentStatus = 'unpaid' | 'paid' | 'refunded';

export interface License {
  id: string;
  key: string;
  customerId: string;
  customer?: { id: string; name: string } | null;
  plan: LicensePlan;
  features: string[];
  domain: string | null;
  maxInstances: number;
  status: LicenseStatus;
  issuedAt: string;
  activatedAt: string | null;
  expiresAt: string;
  revokedAt: string | null;
  revokedReason: string | null;
  price: number;
  paymentStatus: PaymentStatus;
  paymentReference: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export interface ListLicensesQuery {
  page?: number;
  limit?: number;
  customerId?: string;
  status?: LicenseStatus;
  search?: string;
  sort?: string;
}

export interface ListLicensesResponse {
  data: License[];
  meta: { total: number; page: number; limit: number };
}

export interface IssueLicenseInput {
  customerId: string;
  plan: LicensePlan;
  features: string[];
  domain?: string | null;
  maxInstances?: number;
  expiresAt: string; // ISO
  price?: number;
  paymentStatus?: PaymentStatus;
  paymentReference?: string;
  notes?: string;
}

export interface IssueLicenseResponse {
  data: License;
  key: string;
  secret: string;
}

export function listLicenses(query: ListLicensesQuery): Promise<ListLicensesResponse> {
  return getData('/admin/licenses', { params: query });
}

export function issueLicense(input: IssueLicenseInput): Promise<IssueLicenseResponse> {
  return postData('/admin/licenses/issue', input);
}

export function revokeLicense(id: string, reason: string): Promise<{ data: License }> {
  return postData(`/admin/licenses/${id}/revoke`, { reason });
}

export function extendLicense(id: string, addDays: number): Promise<{ data: License }> {
  return postData(`/admin/licenses/${id}/extend`, { addDays });
}

export function regenerateKey(id: string): Promise<{ data: License; key: string }> {
  return postData(`/admin/licenses/${id}/regenerate-key`);
}

export function updateLicense(id: string, input: Partial<IssueLicenseInput>): Promise<{ data: License }> {
  return patchData(`/admin/licenses/${id}`, input);
}
